import React, { useEffect, useState } from 'react';
import { AppState } from '$/store';
import { connect } from 'react-redux';
import Loading from '$/components/loading/Loading';
import * as Bixby from '$/core';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, ColumnApi } from 'ag-grid-enterprise';
import NoDataFound from '$/components/global/noData';
import { getAllCloDealBixbyIdMappings } from '$/store/cloDealBixybyIdMapping/actions';
import { CloDealService } from '$/services';
import EditMapping from '$/pages/admin/clo/edit-mapping';
import { toastError, toastSuccess } from '$/store/toast/actions';
import './index.scss';

interface ICloDealMappingListProps {
  isLoading: boolean;
  error?: Bixby.Models.BixbyError | undefined;
  isSuperAdmin: boolean | undefined;
  cloDealBixbyIdMaps: Bixby.Models.CloDealBixbyIdMapping[] | undefined;
  dispatchGetCloDealHoldingForBixbyIdMappings: () => Promise<void>;
  dispatchToastError: (message: string) => Promise<void>;
  dispatchToastSuccess: (message: string) => Promise<void>;
}

const CloDealManageMappingList: React.FC<ICloDealMappingListProps> = props => {
  const {
    cloDealBixbyIdMaps,
    isLoading,
    dispatchGetCloDealHoldingForBixbyIdMappings,
    isSuperAdmin,
    dispatchToastError,
    dispatchToastSuccess,
  } = props;
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [isEditing, setIsEditing] = useState(false as boolean);
  const [currentMapping, setCurrentMapping] = useState(null as Bixby.Models.CloDealBixbyIdMapping | null);

  const [currentCloDealBixbyIdMappings, setCurrentCloDealBixbyIdMappings] = useState(
    undefined as Bixby.Models.CloDealBixbyIdMapping[] | undefined
  );

  useEffect(() => {
    const getCloDispatcher = async () => {
      await dispatchGetCloDealHoldingForBixbyIdMappings();
    };
    getCloDispatcher();
  }, [dispatchGetCloDealHoldingForBixbyIdMappings]);

  useEffect(() => {
    setCurrentCloDealBixbyIdMappings(cloDealBixbyIdMaps);
  }, [cloDealBixbyIdMaps]);

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const handleDelete = async ({ id }) => {
    try {
      await CloDealService.removeCloBixbyIdMapping(id);
      dispatchToastSuccess('Mapping deleted');
      dispatchGetCloDealHoldingForBixbyIdMappings();
    } catch {
      dispatchToastError('Unable to delete mapping');
    }
  };

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <section className="clo_list">
        {currentCloDealBixbyIdMappings && !Bixby.Utils.isNullOrEmpty(currentCloDealBixbyIdMappings) ? (
          <div className="clo_list_datagrid">
            <div className="grid_container">
              <div className="grid ag-theme-alpine">
                <AgGridReact
                  defaultColDef={{
                    flex: 1,
                    minWidth: 100,
                    sortable: true,
                    resizable: true,
                    lockPinned: true,
                    filter: 'number',
                    cellClass: 'align-center',
                  }}
                  onGridReady={onGridReady}
                  rowData={currentCloDealBixbyIdMappings}
                  suppressDragLeaveHidesColumns={true}
                  suppressMovableColumns={true}
                  animateRows={true}
                  rowHeight={35}
                  headerHeight={35}
                  columnDefs={[
                    {
                      field: 'instrumentId',
                      headerName: 'Instrument Id',
                      width: 120,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      floatingFilter: true,
                    },
                    {
                      field: 'instrumentName',
                      headerName: 'Asset',
                      width: 200,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      valueGetter: props => `${props.data?.issuerName ?? ''} - ${props.data?.instrumentName ?? ''}`,
                      floatingFilter: true,
                    },
                    {
                      field: 'bixbyId',
                      headerName: 'Bixby Id',
                      width: 120,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      floatingFilter: true,
                    },
                    {
                      field: 'figiTicker',
                      headerName: 'Figi Ticker',
                      width: 150,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      floatingFilter: true,
                    },
                    {
                      field: 'companyName',
                      headerName: 'Company Name',
                      width: 150,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      floatingFilter: true,
                    },
                    {
                      field: 'isCompanyActive',
                      headerName: 'Company Active',
                      width: 150,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      floatingFilter: true,
                      valueGetter: params => (params?.data?.isCompanyActive ? 'Active' : 'InActive'),
                    },
                    {
                      field: 'id',
                      headerName: '',
                      floatingFilter: false,
                      suppressColumnsToolPanel: true,
                      width: 150,
                      cellClass: 'align-left',
                      valueGetter: () => '',
                      cellRenderer: (params: any) => {
                        const { id } = params?.data ?? {};

                        return (
                          <button
                            className="bix_grid_button"
                            type="button"
                            id={`edit_button_${id}`}
                            onClick={() => {
                              setCurrentMapping(params.data);
                              setIsEditing(true);
                            }}>
                            Edit
                          </button>
                        );
                      },
                    },
                    {
                      field: 'id',
                      headerName: '',
                      filter: false,
                      floatingFilter: false,
                      suppressColumnsToolPanel: true,
                      width: 150,
                      cellClass: 'align-left',
                      valueGetter: () => '',
                      cellRenderer: (params: any) => {
                        const { id } = params?.data ?? {};

                        return (
                          <button
                            className="bix_grid_button"
                            type="button"
                            id={`remove_button_${id}`}
                            onClick={() => handleDelete(params.data)}>
                            Delete
                          </button>
                        );
                      },
                    },
                  ]}></AgGridReact>
              </div>
            </div>
            {isSuperAdmin && (
              <EditMapping
                dispatchGetCloDealHoldingForBixbyIdMappings={dispatchGetCloDealHoldingForBixbyIdMappings}
                onCloseModal={() => setIsEditing(false)}
                isOpen={isEditing}
                currentMapping={currentMapping}
              />
            )}
          </div>
        ) : (
          <NoDataFound dataType="clo mappings" />
        )}
      </section>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.cloDealBixIdMappingState.isLoading,
    isSuperAdmin: state.profileState.profile?.isSuperAdmin,
    error: state.cloDealHoldingBixbyIdMappingState.error,
    cloDealBixbyIdMaps: state.cloDealBixIdMappingState.cloDealBixbyIdMappings,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetCloDealHoldingForBixbyIdMappings: () => dispatch(getAllCloDealBixbyIdMappings()),
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CloDealManageMappingList);
